import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend-platform/ui-lib/ui';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  type BeforeSaveDevBot,
  DEFAULT_DEV_BOT_NAME,
  readRecentDevBots,
  readStoredDevBotId,
  writeDevBotDraft,
} from './dev-bot-storage';
import { pushDevSession } from './dev-session';
import { describeTokenHealth, getTokenHealth } from './dev-token';
import { NetomiLogoWhite } from './netomi-logo';
import type { SidebarItemNode, SidebarNode, SidebarSectionNode, SidebarSubItemNode } from './types';

export type DevShellLinkRenderer = (props: {
  href: string;
  className: string;
  children: ReactNode;
  /** Accessible name for icon-only links (the collapsed rail). */
  label?: string;
}) => ReactNode;

const renderAnchor: DevShellLinkRenderer = ({ href, className, children, label }) => (
  <a href={href} className={className} aria-label={label} title={label}>
    {children}
  </a>
);

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function matches(
  item: { href?: string; isActive?: (pathname: string) => boolean },
  pathname: string
) {
  if (item.isActive) return item.isActive(pathname);
  return item.href ? isActivePath(pathname, item.href) : false;
}

interface NavContext {
  pathname: string;
  renderLink: DevShellLinkRenderer;
}

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30';

function rowStateClass(isActive: boolean) {
  return isActive
    ? 'rounded-md border-steel-blue-45 bg-white'
    : 'rounded-sm border-transparent hover:bg-steel-blue-15';
}

function SubItem({ item, nav }: { item: SidebarSubItemNode; nav: NavContext }) {
  const { label, href } = item;
  const isActive = matches(item, nav.pathname);
  const className = `flex h-8 w-full shrink-0 select-none items-center overflow-hidden border px-2 text-left text-steel-light transition-colors ${FOCUS_RING} ${rowStateClass(isActive)}`;
  const content = (
    <span className="min-w-0 flex-1 truncate font-medium text-sm leading-4.5">{label}</span>
  );

  if (href) {
    return nav.renderLink({ href, className, children: content });
  }
  return <div className={className}>{content}</div>;
}

function NavItem({ item, nav }: { item: SidebarItemNode; nav: NavContext }) {
  const { icon: Icon, label, href, expandable, children, defaultExpanded = false } = item;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasChildren = Boolean(children && children.length > 0);
  const isActive = matches(item, nav.pathname);
  const className = `flex h-9 w-full shrink-0 select-none items-center gap-2 overflow-hidden border px-2 text-left text-black transition-colors ${FOCUS_RING} ${rowStateClass(isActive)}`;
  const Chevron = expanded ? ChevronUp : ChevronDown;
  const content = (
    <>
      <Icon size={16} className="shrink-0 text-steel-light" />
      <span className="min-w-0 flex-1 truncate font-medium text-sm leading-4.5">{label}</span>
      {(hasChildren || expandable) && <Chevron size={16} className="shrink-0 text-steel-light" />}
    </>
  );

  if (hasChildren) {
    return (
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          className={className}
        >
          {content}
        </button>
        {expanded && (
          <div className="relative flex flex-col gap-0.5 pl-4">
            <div aria-hidden className="absolute inset-y-0 left-2 w-px bg-steel-blue-45" />
            {children?.map((child) => (
              <SubItem key={child.label} item={child} nav={nav} />
            ))}
          </div>
        )}
      </div>
    );
  }
  if (href) {
    return nav.renderLink({ href, className, children: content });
  }
  return <div className={className}>{content}</div>;
}

function SidebarSection({
  section: { label, defaultExpanded = false, children },
  nav,
}: {
  section: SidebarSectionNode;
  nav: NavContext;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const Chevron = expanded ? ChevronUp : ChevronDown;

  return (
    <div className="mb-1 flex flex-col gap-0.5">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        className="flex h-9 w-full select-none items-center gap-1 overflow-hidden rounded-sm pr-2 pl-4 text-black transition-colors hover:bg-steel-blue-15"
      >
        <Chevron size={12} className="shrink-0 text-steel-light" />
        <span className="min-w-0 flex-1 truncate text-left font-semibold text-sm leading-4.5">
          {label}
        </span>
      </button>
      {expanded && children && children.length > 0 && (
        <div className="flex flex-col gap-0.5 pl-6">
          {children.map((item) => (
            <NavItem key={item.label} item={item} nav={nav} />
          ))}
        </div>
      )}
    </div>
  );
}

function BotSwitcher({
  botId,
  onBeforeSaveBot,
}: {
  botId: string;
  onBeforeSaveBot?: BeforeSaveDevBot;
}) {
  const recent = readRecentDevBots();

  async function switchTo(entry: { accessToken: string; botId: string }) {
    if (!writeDevBotDraft(entry, onBeforeSaveBot)) return;
    await pushDevSession();
    window.location.reload();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Switch AI Agent"
        className="flex w-full items-center gap-4 rounded-md p-2 text-left transition-colors hover:bg-steel-blue-15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30"
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-steel-ultradark">
          <NetomiLogoWhite />
        </div>
        <div className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-sm text-steel-dark leading-[1.4]">
            {DEFAULT_DEV_BOT_NAME}
          </span>
          {botId && (
            <span className="block truncate text-steel-blue text-xs leading-[1.4]">
              ID: {botId}
            </span>
          )}
        </div>
        <ChevronsUpDown size={14} className="shrink-0 text-steel-light" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>Recent AI Agents</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recent.length === 0 ? (
          <DropdownMenuItem disabled>
            Save an AI Agent with Add AI Agent to switch here.
          </DropdownMenuItem>
        ) : (
          recent.map((entry) => (
            <DropdownMenuItem
              key={entry.botId}
              disabled={entry.botId === botId}
              onSelect={() => switchTo(entry)}
              className="flex flex-col items-start gap-0.5"
            >
              <span className="w-full truncate font-medium text-sm">{entry.botId}</span>
              <span className="w-full truncate text-steel-light text-xs">
                {entry.botId === botId ? 'Current. ' : ''}
                {describeTokenHealth(getTokenHealth(entry.accessToken))}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface DevShellRailProps {
  id?: string;
  nodes: SidebarNode[];
  pathname: string;
  renderLink?: DevShellLinkRenderer;
}

function RailItem({ item, nav }: { item: SidebarItemNode; nav: NavContext }) {
  const isActive =
    matches(item, nav.pathname) ||
    Boolean(item.children?.some((child) => matches(child, nav.pathname)));
  const className = `flex size-8 shrink-0 items-center justify-center border transition-colors ${FOCUS_RING} ${rowStateClass(isActive)}`;
  const icon = <item.icon size={16} className="text-steel-light" aria-hidden />;
  const href = item.href ?? item.children?.find((child) => child.href)?.href;

  if (href) {
    return nav.renderLink({ href, className, children: icon, label: item.label });
  }
  return (
    <span role="img" aria-label={item.label} title={item.label} className={className}>
      {icon}
    </span>
  );
}

/** aistudio's collapsed sidebar: a 48px rail of first-level item icons. */
export function DevShellRail({
  id,
  nodes,
  pathname,
  renderLink = renderAnchor,
}: DevShellRailProps) {
  const nav: NavContext = { pathname, renderLink };
  const groups = nodes
    .map((node) => (node.kind === 'section' ? (node.children ?? []) : [node]))
    .filter((items) => items.length > 0);

  return (
    <aside
      id={id}
      aria-label="Collapsed navigation"
      className="flex h-full w-12 shrink-0 flex-col items-center gap-1 overflow-y-auto border-steel-blue-45 border-r bg-steel-blue-5 py-3"
    >
      <div className="mb-2 flex size-8 shrink-0 items-center justify-center rounded-lg bg-steel-ultradark">
        <NetomiLogoWhite />
      </div>
      {groups.map((items, index) => (
        <div
          key={items.map((item) => item.label).join('|')}
          className="flex flex-col items-center gap-0.5"
        >
          {index > 0 ? <div className="my-1 h-px w-8 bg-steel-blue-45" /> : null}
          {items.map((item) => (
            <RailItem key={item.label} item={item} nav={nav} />
          ))}
        </div>
      ))}
    </aside>
  );
}

export interface DevShellSidebarProps {
  id?: string;
  nodes: SidebarNode[];
  pathname: string;
  /** Defaults to a plain `<a href>`; router-aware shells pass their `<Link>`. */
  renderLink?: DevShellLinkRenderer;
  onBeforeSaveBot?: BeforeSaveDevBot;
}

export function DevShellSidebar({
  id,
  nodes,
  pathname,
  renderLink = renderAnchor,
  onBeforeSaveBot,
}: DevShellSidebarProps) {
  const botId = readStoredDevBotId();
  const nav: NavContext = { pathname, renderLink };

  return (
    <aside
      id={id}
      className="flex h-full w-64 shrink-0 flex-col overflow-hidden border-steel-blue-45 border-r bg-steel-blue-5"
    >
      <div className="shrink-0 px-4 py-3">
        <BotSwitcher botId={botId} onBeforeSaveBot={onBeforeSaveBot} />
      </div>

      <div className="mx-4 h-px shrink-0 bg-steel-blue-45" />

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-2">
        {nodes.map((node) =>
          node.kind === 'section' ? (
            <SidebarSection key={node.label} section={node} nav={nav} />
          ) : (
            <NavItem key={node.label} item={node} nav={nav} />
          )
        )}
      </div>
    </aside>
  );
}
